import React, { useEffect, useState } from 'react';
import {
  getSlackConnectionStatus,
  disconnectSlack,
  connectSlackPopup,
  SlackConnectionStatus,
} from '../../services/api';
import { getSlackChannels, SlackChannel } from '../../services/scheduledTasksApi';

const DAYS_BACK_OPTIONS = [
  { value: 1, label: '1 day' },
  { value: 3, label: '3 days' },
  { value: 7, label: '7 days (recommended)' },
  { value: 14, label: '14 days' },
  { value: 30, label: '30 days' },
];

export interface SlackConfigData {
  scope: 'mentions-and-dms' | 'specific-channels';
  channelIds: string[];
  daysBack: number;
  customSubject?: string;
}

interface SlackConfigProps {
  config: SlackConfigData;
  onChange: (config: SlackConfigData) => void;
  userEmail: string;
}

const SlackConfig: React.FC<SlackConfigProps> = ({ config, onChange, userEmail }) => {
  const [status, setStatus] = useState<SlackConnectionStatus>({ connected: false });
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [channels, setChannels] = useState<SlackChannel[]>([]);
  const [loadingChannels, setLoadingChannels] = useState(false);
  const [channelsError, setChannelsError] = useState<string | null>(null);

  const refreshStatus = async () => {
    try {
      setLoadingStatus(true);
      const s = await getSlackConnectionStatus();
      setStatus(s);
    } catch {
      setStatus({ connected: false });
    } finally {
      setLoadingStatus(false);
    }
  };

  useEffect(() => {
    refreshStatus();
  }, []);

  useEffect(() => {
    if (!status.connected) {
      setChannels([]);
      return;
    }
    if (config.scope !== 'specific-channels') return;
    setLoadingChannels(true);
    setChannelsError(null);
    getSlackChannels()
      .then((ch) => setChannels(ch))
      .catch((err: Error) => setChannelsError(err.message))
      .finally(() => setLoadingChannels(false));
  }, [status.connected, config.scope]);

  const handleConnect = async () => {
    try {
      setConnecting(true);
      await connectSlackPopup();
      await refreshStatus();
    } catch (err: any) {
      const msg = String(err?.message || err);
      if (!/cancel/i.test(msg)) alert(`Slack connection failed: ${msg}`);
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect Slack? Any tasks using Slack will fail until you reconnect.')) return;
    try {
      await disconnectSlack();
      await refreshStatus();
    } catch (err: any) {
      alert(`Failed to disconnect: ${err.message || err}`);
    }
  };

  const toggleChannel = (id: string) => {
    const next = config.channelIds.includes(id)
      ? config.channelIds.filter((c) => c !== id)
      : [...config.channelIds, id];
    onChange({ ...config, channelIds: next });
  };

  const styles: { [key: string]: React.CSSProperties } = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      gap: '1.25rem',
      padding: '1rem',
      backgroundColor: 'var(--cg-neutral-25)',
      borderRadius: '8px',
      border: '1px solid var(--border-1)',
    },
    sectionTitle: {
      fontSize: '0.875rem',
      fontWeight: 600,
      color: 'var(--fg-1)',
      marginBottom: '0.25rem',
    },
    formGroup: { display: 'flex', flexDirection: 'column', gap: '0.5rem' },
    label: { fontSize: '0.875rem', fontWeight: 500, color: 'var(--fg-1)' },
    helpText: { color: 'var(--fg-2)', fontSize: '0.75rem', marginTop: '0.25rem' },
    select: {
      padding: '0.625rem 0.75rem',
      borderRadius: '6px',
      border: '1px solid var(--border-1)',
      fontSize: '0.875rem',
      backgroundColor: 'var(--bg-surface-1)',
      color: 'var(--fg-1)',
    },
    input: {
      padding: '0.625rem 0.75rem',
      borderRadius: '6px',
      border: '1px solid var(--border-1)',
      fontSize: '0.875rem',
      backgroundColor: 'var(--bg-surface-1)',
      color: 'var(--fg-1)',
    },
    connectBar: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '0.75rem',
      padding: '0.75rem 1rem',
      border: '1px solid var(--border-1)',
      borderRadius: '6px',
      backgroundColor: 'var(--bg-surface-1)',
    },
    connectedDot: {
      display: 'inline-block',
      width: '8px',
      height: '8px',
      borderRadius: '50%',
      backgroundColor: 'var(--success)',
      marginRight: '0.5rem',
    },
    disconnectedDot: {
      display: 'inline-block',
      width: '8px',
      height: '8px',
      borderRadius: '50%',
      backgroundColor: 'var(--warning)',
      marginRight: '0.5rem',
    },
    primaryBtn: {
      padding: '0.5rem 1rem',
      borderRadius: '6px',
      border: 'none',
      backgroundColor: '#4A154B',
      color: 'white',
      fontSize: '0.8125rem',
      fontWeight: 600,
      cursor: 'pointer',
      whiteSpace: 'nowrap',
    },
    secondaryBtn: {
      padding: '0.4rem 0.75rem',
      borderRadius: '6px',
      border: '1px solid var(--border-1)',
      background: 'transparent',
      color: 'var(--fg-2)',
      fontSize: '0.75rem',
      cursor: 'pointer',
    },
    radioRow: { display: 'flex', flexDirection: 'column', gap: '0.5rem' },
    radioItem: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '0.5rem',
      padding: '0.625rem',
      border: '1px solid var(--border-1)',
      borderRadius: '6px',
      cursor: 'pointer',
    },
    channelList: {
      maxHeight: '220px',
      overflow: 'auto',
      border: '1px solid var(--border-1)',
      borderRadius: '6px',
      backgroundColor: 'var(--bg-surface-1)',
    },
    channelItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '0.5rem 0.75rem',
      borderBottom: '1px solid var(--border-1)',
      fontSize: '0.8125rem',
    },
    deliveryNote: {
      padding: '0.5rem 0.75rem',
      backgroundColor: 'rgba(74, 21, 75, 0.06)',
      borderRadius: '6px',
      fontSize: '0.75rem',
      color: 'var(--fg-2)',
    },
  };

  return (
    <div style={styles.container}>
      <div style={styles.sectionTitle}>Slack Recap Settings</div>

      {/* Slack connection status + Connect button */}
      <div style={styles.formGroup}>
        <label style={styles.label}>Slack Connection</label>
        <div style={styles.connectBar}>
          <div>
            {loadingStatus ? (
              <span style={{ fontSize: '0.8125rem' }}>Checking status…</span>
            ) : status.connected ? (
              <span style={{ fontSize: '0.8125rem' }}>
                <span style={styles.connectedDot} />
                Connected{status.teamName ? ` to ${status.teamName}` : ''}
              </span>
            ) : (
              <span style={{ fontSize: '0.8125rem' }}>
                <span style={styles.disconnectedDot} />
                Not connected
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {status.connected && (
              <button type="button" style={styles.secondaryBtn} onClick={handleDisconnect}>
                Disconnect
              </button>
            )}
            <button
              type="button"
              style={styles.primaryBtn}
              onClick={handleConnect}
              disabled={connecting}
            >
              {connecting ? 'Opening…' : status.connected ? 'Reconnect Slack' : 'Connect Slack'}
            </button>
          </div>
        </div>
        {!status.connected && (
          <p style={styles.helpText}>
            Required to read messages. You'll sign in to Slack in a popup.
          </p>
        )}
      </div>

      {/* Scope */}
      <div style={styles.formGroup}>
        <label style={styles.label}>What should the digest cover?</label>
        <div style={styles.radioRow}>
          <label style={styles.radioItem}>
            <input
              type="radio"
              checked={config.scope === 'mentions-and-dms'}
              onChange={() => onChange({ ...config, scope: 'mentions-and-dms', channelIds: [] })}
            />
            <span>
              <strong>Mentions + my DMs</strong>
              <div style={styles.helpText}>
                @mentions of you across the workspace, plus every DM / group-DM in the window.
              </div>
            </span>
          </label>
          <label style={styles.radioItem}>
            <input
              type="radio"
              checked={config.scope === 'specific-channels'}
              onChange={() => onChange({ ...config, scope: 'specific-channels' })}
            />
            <span>
              <strong>Specific channels</strong>
              <div style={styles.helpText}>Summarize activity in a set of channels you pick.</div>
            </span>
          </label>
        </div>
      </div>

      {/* Channel picker (only when scope = specific-channels) */}
      {config.scope === 'specific-channels' && (
        <div style={styles.formGroup}>
          <label style={styles.label}>
            Channels ({config.channelIds.length} selected)
          </label>
          {!status.connected && (
            <p style={styles.helpText}>Connect Slack first to load your channels.</p>
          )}
          {status.connected && loadingChannels && (
            <p style={styles.helpText}>Loading channels…</p>
          )}
          {status.connected && channelsError && (
            <p style={{ ...styles.helpText, color: 'var(--danger)' }}>Failed to load channels: {channelsError}</p>
          )}
          {status.connected && !loadingChannels && channels.length > 0 && (
            <div style={styles.channelList}>
              {channels.map((c) => (
                <label key={c.id} style={styles.channelItem}>
                  <input
                    type="checkbox"
                    checked={config.channelIds.includes(c.id)}
                    onChange={() => toggleChannel(c.id)}
                  />
                  <span>
                    {c.isPrivate ? '🔒 ' : c.isMpim ? '👥 ' : '# '}
                    {c.name}
                  </span>
                </label>
              ))}
            </div>
          )}
          {status.connected && !loadingChannels && channels.length === 0 && !channelsError && (
            <p style={styles.helpText}>No channels found — are you a member of any?</p>
          )}
        </div>
      )}

      {/* Days back */}
      <div style={styles.formGroup}>
        <label style={styles.label}>Include Slack activity from last</label>
        <select
          style={styles.select}
          value={config.daysBack}
          onChange={(e) => onChange({ ...config, daysBack: Number(e.target.value) })}
        >
          {DAYS_BACK_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {/* Custom subject */}
      <div style={styles.formGroup}>
        <label style={styles.label}>Email Subject (optional)</label>
        <input
          style={styles.input}
          type="text"
          placeholder="Slack Recap (Feb 11 - Feb 18, 2026)"
          value={config.customSubject || ''}
          onChange={(e) => onChange({ ...config, customSubject: e.target.value || undefined })}
          maxLength={200}
        />
      </div>

      {/* Delivery note */}
      <div style={styles.deliveryNote}>
        Digest will be emailed to <strong>{userEmail}</strong>.
      </div>
    </div>
  );
};

export default SlackConfig;
